import React, { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useLazyQuery, useMutation } from '@apollo/client/react';
import CoreCheckbox from '@components-clean/core/core-checkbox';
import EditCard from '@components-clean/core/edit-card';
import Loading from '@components-clean/core/loading/loading';
import { useUnsavedChangesContext } from '@contexts-clean/unsaved-changes-context';
import styles from '@pages-clean/admin/study/manage-hubs/central-monitoring/style.module.scss';
import { useBoolean, useTranslation } from '@hooks/index';
import { ShowError, ShowSuccess } from '@utils/notification';
import { ADD_STUDY_HUB_SITE_FIELDS_CONFIG, GET_STUDY_HUB_SITE_FIELDS_CONFIG } from '@queries/central-monitoring';
import {
    getStudyHubSiteFieldsConfig,
    getStudyHubSiteFieldsConfig_OpraQueries_getStudyHubSiteFieldsConfig_StudyHubSiteFieldsConfig as StudyHubSiteFieldsConfig,
    getStudyHubSiteFieldsConfigVariables,
    upsertStudyHubSiteFieldsConfig,
    upsertStudyHubSiteFieldsConfigVariables,
} from '@queries/generated/central-monitoring.generated';
import { studyHubSiteFieldsConfigFields } from '@queries/generated/fragments.generated';

const COMPONENT_NAME = 'studyHubSiteFields';

interface StudyFieldsConfigProps {
    studyRef: string;
    canEdit: boolean;
}

const StudyHubSiteFields = ({ studyRef, canEdit }: StudyFieldsConfigProps) => {
    const { confirmDiscardChanges, setComponentHasChanges, componentHasChanges } = useUnsavedChangesContext();

    const {
        t,
        i18n: { t: tRoot },
    } = useTranslation('admin', {
        keyPrefix: 'studyHubSiteFields',
    });
    const isEditMode = useBoolean(false);

    const [loafConfig, { data, loading: siteFieldsConfigLoading, refetch }] = useLazyQuery<
        getStudyHubSiteFieldsConfig,
        getStudyHubSiteFieldsConfigVariables
    >(GET_STUDY_HUB_SITE_FIELDS_CONFIG);

    const [saveConfig, { loading: isMutatingSiteFieldConfigData }] = useMutation<
        upsertStudyHubSiteFieldsConfig,
        upsertStudyHubSiteFieldsConfigVariables
    >(ADD_STUDY_HUB_SITE_FIELDS_CONFIG);

    useEffect(() => {
        loafConfig({ variables: { studyId: studyRef } }).catch((err) => window.console.error(err));
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [studyRef]);

    const defaultValues = {
        score: data?.getStudyHubSiteFieldsConfig?.score,
        numGreenKRIs: data?.getStudyHubSiteFieldsConfig?.numGreenKRIs,
        numAmberKRIs: data?.getStudyHubSiteFieldsConfig?.numAmberKRIs,
        numRedKRIs: data?.getStudyHubSiteFieldsConfig?.numRedKRIs,
        numGreenDecisionMakingKRIs: data?.getStudyHubSiteFieldsConfig?.numGreenDecisionMakingKRIs,
        numAmberDecisionMakingKRIs: data?.getStudyHubSiteFieldsConfig?.numAmberDecisionMakingKRIs,
        numRedDecisionMakingKRIs: data?.getStudyHubSiteFieldsConfig?.numRedDecisionMakingKRIs,
        activeDate: data?.getStudyHubSiteFieldsConfig?.activeDate,
        closedDate: data?.getStudyHubSiteFieldsConfig?.closedDate,
        investigator: data?.getStudyHubSiteFieldsConfig?.investigator,
        monitor: data?.getStudyHubSiteFieldsConfig?.monitor,
    };

    const {
        control,
        reset,
        handleSubmit,
        formState: { isDirty },
    } = useForm<StudyHubSiteFieldsConfig>({ defaultValues });

    useEffect(() => {
        const score = data?.getStudyHubSiteFieldsConfig?.score;
        const numGreenKRIs = data?.getStudyHubSiteFieldsConfig?.numGreenKRIs;
        const numAmberKRIs = data?.getStudyHubSiteFieldsConfig?.numAmberKRIs;
        const numRedKRIs = data?.getStudyHubSiteFieldsConfig?.numRedKRIs;
        const numGreenDecisionMakingKRIs = data?.getStudyHubSiteFieldsConfig?.numGreenDecisionMakingKRIs;
        const numAmberDecisionMakingKRIs = data?.getStudyHubSiteFieldsConfig?.numAmberDecisionMakingKRIs;
        const numRedDecisionMakingKRIs = data?.getStudyHubSiteFieldsConfig?.numRedDecisionMakingKRIs;
        const activeDate = data?.getStudyHubSiteFieldsConfig?.activeDate;
        const closedDate = data?.getStudyHubSiteFieldsConfig?.closedDate;
        const investigator = data?.getStudyHubSiteFieldsConfig?.investigator;
        const monitor = data?.getStudyHubSiteFieldsConfig?.monitor;

        reset(
            {
                score,
                numGreenKRIs,
                numAmberKRIs,
                numRedKRIs,
                numGreenDecisionMakingKRIs,
                numAmberDecisionMakingKRIs,
                numRedDecisionMakingKRIs,
                activeDate,
                closedDate,
                investigator,
                monitor,
            },
            { keepDirty: false },
        );
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [data]);

    useEffect(() => {
        setComponentHasChanges(COMPONENT_NAME, isDirty);
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [isDirty]);

    useEffect(() => {
        return () => {
            setComponentHasChanges(COMPONENT_NAME, false);
        };
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, []);

    const onSubmit = handleSubmit(async (saveData: studyHubSiteFieldsConfigFields) => {
        if (isDirty) {
            saveConfig({
                variables: {
                    studyId: studyRef,
                    studyHubSiteFieldsConfig: {
                        score: saveData?.score,
                        numGreenKRIs: saveData?.numGreenKRIs,
                        numAmberKRIs: saveData?.numAmberKRIs,
                        numRedKRIs: saveData?.numRedKRIs,
                        numGreenDecisionMakingKRIs: saveData?.numGreenDecisionMakingKRIs,
                        numAmberDecisionMakingKRIs: saveData?.numAmberDecisionMakingKRIs,
                        numRedDecisionMakingKRIs: saveData?.numRedDecisionMakingKRIs,
                        activeDate: saveData?.activeDate,
                        closedDate: saveData?.closedDate,
                        investigator: saveData?.investigator,
                        monitor: saveData?.monitor,
                    },
                },
            })
                .then(async () => {
                    isEditMode.setFalse();
                    ShowSuccess(tRoot('actionsSuccessfulToast'));
                    await refetch();
                })
                .catch(() => {
                    ShowError(t('errorMessage'));
                });
        } else {
            isEditMode.setFalse();
        }
    });

    useEffect(() => {
        if (!isEditMode.value) {
            setComponentHasChanges(COMPONENT_NAME, false);
        }
        // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
        // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, [isEditMode.value]);

    const isLoading = !data || siteFieldsConfigLoading || isMutatingSiteFieldConfigData;
    return (
        <div>
            <EditCard
                title={t('siteFields')}
                onEdit={() => isEditMode.setTrue()}
                onCancel={async () => {
                    if (componentHasChanges(COMPONENT_NAME)) {
                        const ok = await confirmDiscardChanges();
                        if (!ok) {
                            return;
                        }
                    }
                    reset(defaultValues, { keepDirty: false });
                    isEditMode.setFalse();
                }}
                onSave={onSubmit}
                isEditMode={isEditMode.value}
                disabled={!canEdit || isLoading}
                data-testid="panels-config"
            >
                {isLoading && <Loading />}
                {!isLoading && (
                    <div className={styles.checkboxHolder}>
                        <div className={styles.checkboxItem}>
                            <Controller
                                control={control}
                                name="score"
                                render={({ field: { onChange, value } }) => (
                                    <CoreCheckbox
                                        value={value}
                                        onChange={onChange}
                                        disabled={!canEdit || isLoading || !isEditMode.value}
                                        label={t('fields.score')}
                                        data-testid="checkbox-score"
                                    />
                                )}
                            />
                        </div>
                        <div className={styles.checkboxItem}>
                            <Controller
                                control={control}
                                name="numGreenKRIs"
                                render={({ field: { onChange, value } }) => (
                                    <CoreCheckbox
                                        value={value}
                                        onChange={onChange}
                                        disabled={!canEdit || isLoading || !isEditMode.value}
                                        label={t('fields.numGreenKRIs')}
                                        data-testid="checkbox-numGreenKRIs"
                                    />
                                )}
                            />
                        </div>
                        <div className={styles.checkboxItem}>
                            <Controller
                                control={control}
                                name="numAmberKRIs"
                                render={({ field: { onChange, value } }) => (
                                    <CoreCheckbox
                                        value={value}
                                        onChange={onChange}
                                        disabled={!canEdit || isLoading || !isEditMode.value}
                                        label={t('fields.numAmberKRIs')}
                                        data-testid="checkbox-numAmberKRIs"
                                    />
                                )}
                            />
                        </div>
                        <div className={styles.checkboxItem}>
                            <Controller
                                control={control}
                                name="numRedKRIs"
                                render={({ field: { onChange, value } }) => (
                                    <CoreCheckbox
                                        value={value}
                                        onChange={onChange}
                                        disabled={!canEdit || isLoading || !isEditMode.value}
                                        label={t('fields.numRedKRIs')}
                                        data-testid="checkbox-numRedKRIs"
                                    />
                                )}
                            />
                        </div>
                        <div className={styles.checkboxItem}>
                            <Controller
                                control={control}
                                name="numGreenDecisionMakingKRIs"
                                render={({ field: { onChange, value } }) => (
                                    <CoreCheckbox
                                        value={value}
                                        onChange={onChange}
                                        disabled={!canEdit || isLoading || !isEditMode.value}
                                        label={t('fields.numGreenDecisionMakingKRIs')}
                                        data-testid="checkbox-numGreenDecisionMakingKRIs"
                                    />
                                )}
                            />
                        </div>
                        <div className={styles.checkboxItem}>
                            <Controller
                                control={control}
                                name="numAmberDecisionMakingKRIs"
                                render={({ field: { onChange, value } }) => (
                                    <CoreCheckbox
                                        value={value}
                                        onChange={onChange}
                                        disabled={!canEdit || isLoading || !isEditMode.value}
                                        label={t('fields.numAmberDecisionMakingKRIs')}
                                        data-testid="checkbox-numAmberDecisionMakingKRIs"
                                    />
                                )}
                            />
                        </div>
                        <div className={styles.checkboxItem}>
                            <Controller
                                control={control}
                                name="numRedDecisionMakingKRIs"
                                render={({ field: { onChange, value } }) => (
                                    <CoreCheckbox
                                        value={value}
                                        onChange={onChange}
                                        disabled={!canEdit || isLoading || !isEditMode.value}
                                        label={t('fields.numRedDecisionMakingKRIs')}
                                        data-testid="checkbox-numRedDecisionMakingKRIs"
                                    />
                                )}
                            />
                        </div>
                        <div className={styles.checkboxItem}>
                            <Controller
                                control={control}
                                name="activeDate"
                                render={({ field: { onChange, value } }) => (
                                    <CoreCheckbox
                                        value={value}
                                        onChange={onChange}
                                        disabled={!canEdit || isLoading || !isEditMode.value}
                                        label={t('fields.activeDate')}
                                        data-testid="checkbox-activeDate"
                                    />
                                )}
                            />
                        </div>
                        <div className={styles.checkboxItem}>
                            <Controller
                                control={control}
                                name="closedDate"
                                render={({ field: { onChange, value } }) => (
                                    <CoreCheckbox
                                        value={value}
                                        onChange={onChange}
                                        disabled={!canEdit || isLoading || !isEditMode.value}
                                        label={t('fields.closedDate')}
                                        data-testid="checkbox-closedDate"
                                    />
                                )}
                            />
                        </div>
                        <div className={styles.checkboxItem}>
                            <Controller
                                control={control}
                                name="investigator"
                                render={({ field: { onChange, value } }) => (
                                    <CoreCheckbox
                                        value={value}
                                        onChange={onChange}
                                        disabled={!canEdit || isLoading || !isEditMode.value}
                                        label={t('fields.investigator')}
                                        data-testid="checkbox-investigator"
                                    />
                                )}
                            />
                        </div>
                        <div className={styles.checkboxItem}>
                            <Controller
                                control={control}
                                name="monitor"
                                render={({ field: { onChange, value } }) => (
                                    <CoreCheckbox
                                        value={value}
                                        onChange={onChange}
                                        disabled={!canEdit || isLoading || !isEditMode.value}
                                        label={t('fields.monitor')}
                                        data-testid="checkbox-monitor"
                                    />
                                )}
                            />
                        </div>
                    </div>
                )}
            </EditCard>
        </div>
    );
};

export default StudyHubSiteFields;
